/**
 * Light Theme Token Values
 *
 * Frosted glass on light backgrounds with shadow-based depth.
 * Same structural shape as darkTheme but with inverted luminance.
 */
import { PRIMITIVE_COLORS } from '../../residents/residentColors';
import { adminLightComponents } from '../../admin/theme/adminLight';
import { TYPOGRAPHY } from './typography';
import { SPACING } from './spacing';
import { RADIUS } from './radius';
import { SHADOW_SHAPES } from './shadow';
import { MOTION } from './motion';
import type { AppTheme } from './types';

export const lightTheme: AppTheme = {
  mode: 'light',

  redesign: {
    bg: {
      base: '#F8F9FA',
      modal: '#FFFFFF',
      gradientStart: '#EEF0F5',
    },
    card: {
      bg: '#FFFFFF',
      bgStrong: '#FFFFFF',
      border: 'rgba(0,0,0,0.08)',
      borderSolid: 'rgba(0,0,0,0.06)',
      borderDashed: 'rgba(0,0,0,0.12)',
      borderHover: 'rgba(0,0,0,0.14)',
      shadowHover: '0 6px 20px rgba(0,0,0,0.12)',
    },
    glass: {
      buttonBg: 'rgba(0,0,0,0.03)',
      buttonBorder: 'rgba(0,0,0,0.06)',
      buttonBgImage: 'rgba(255,255,255,0.65)',
      buttonBorderImage: 'rgba(0,0,0,0.08)',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#333333',
      tertiary: '#444444',
      muted: '#777777',
      subtle: '#999999',
      dim: '#AAAAAA',
      dimmer: '#BBBBBB',
      imageSubtitle: 'rgba(0,0,0,0.5)',
    },
    separator: 'rgba(0,0,0,0.08)',
    activityIcon: {
      bg: 'rgba(0,0,0,0.04)',
      border: 'rgba(0,0,0,0.06)',
    },
    status: {
      inProgress: 'rgba(255,118,41,0.1)',
      inProgressText: '#E06A20',
    },
    accent: {
      green: '#4CAF50',
      greenBg: 'rgba(76,175,80,0.1)',
      greenLight: 'rgba(76,175,80,0.08)',
      red: '#E53E3E',
      redBg: 'rgba(229,62,62,0.08)',
      orange: 'rgba(237,137,54,0.1)',
      orangeLight: 'rgba(237,137,54,0.08)',
      purple: 'rgba(159,68,211,0.1)',
      purpleLight: 'rgba(159,68,211,0.08)',
      blueBg: 'rgba(59,100,234,0.1)',
      blueBgLight: 'rgba(59,100,234,0.08)',
      blueBgMedium: 'rgba(59,100,234,0.12)',
      blueBgStrong: 'rgba(59,100,234,0.15)',
    },
    newsfeed: {
      announcementBg: 'rgba(240,243,255,0.92)',
      announcementBorder: 'rgba(59,100,234,0.2)',
      announcementBadgeBg: 'rgba(59,100,234,0.08)',
      tagActiveText: '#3B64EA',
      tagActiveBg: 'rgba(59,100,234,0.1)',
      tagActiveBorder: 'rgba(59,100,234,0.25)',
    },
    gradient: {
      redGlow: { color: 'rgba(245,85,96,0.16)', midColor: 'rgba(245,85,96,0.08)' },
      purpleGlow: { color: 'rgba(190,94,200,0.14)' },
      blueGlowTop: { color: 'rgba(71,115,254,0.22)', midColor: 'rgba(71,115,254,0.12)' },
      blueGlowBottom: { color: 'rgba(71,115,254,0.12)' },
      imageOverlay: [
        'rgba(248,249,250,0.15)',
        'rgba(248,249,250,0.35)',
        'rgba(248,249,250,0.55)',
        'rgba(248,249,250,0.72)',
        'rgba(248,249,250,0.85)',
        'rgba(248,249,250,0.95)',
        'rgb(248,249,250)',
      ],
    },
    sidebar: {
      itemHoverBg: 'rgba(0,0,0,0.03)',
      itemActiveBg: 'rgba(70,114,254,0.10)',
      indicator: PRIMITIVE_COLORS.resioBlue,
    },
  },

  semantic: {
    background: {
      primary: '#F8F9FA',
      secondary: '#FFFFFF',
      tertiary: '#F0F1F3',
      input: '#FFFFFF',
      overlay: 'rgba(0,0,0,0.35)',
      hover: 'rgba(0,0,0,0.04)',
    },
    text: {
      primary: '#1A1A1A',
      secondary: '#555555',
      tertiary: '#777777',
      muted: '#999999',
      light: '#333333',
      inverse: '#FFFFFF',
      link: PRIMITIVE_COLORS.resioBlue,
    },
    border: {
      primary: PRIMITIVE_COLORS.resioBlue,
      secondary: '#E0E0E0',
      tertiary: '#EEEEEE',
      input: '#CCCCCC',
      focus: PRIMITIVE_COLORS.resioBlue,
    },
    interactive: {
      primary: PRIMITIVE_COLORS.resioBlue,
      // Darker than `primary`, not lighter. This slot held `resioBlueLight`, which
      // is the correct direction in dark and exactly backwards here: on a light
      // surface a lighter blue loses contrast instead of gaining emphasis. It
      // measured 2.88:1 on the page background and 3.03:1 on a card, below the
      // 4.5:1 floor for the text and icon slots that reach for this token
      // (desktop `TEXT_SECONDARY`, marketplace `accentBlue`, the property-chat
      // label and file-upload icons). `resioBlueDark` puts those at 6.35/6.69.
      primaryHover: PRIMITIVE_COLORS.resioBlueDark,
      secondary: PRIMITIVE_COLORS.resioPink,
      danger: PRIMITIVE_COLORS.resioDangerRed,
      disabled: '#BBBBBB',
    },
    status: {
      success: '#4CAF50',
      successBg: 'rgba(76,175,80,0.1)',
      // Darker than `success` for the same reason `errorText` is darker than
      // `error`: 5.13:1 on a white card, where `success` itself manages 2.78:1.
      successText: '#2E7D32',
      warning: '#E06A20',
      error: '#E53E3E',
      // Pairs at 4.77:1. The ink is deliberately darker than `error`, which
      // measures only about 3.5:1 on this surface.
      errorBg: '#FDECEA',
      errorText: '#C92A2A',
      info: PRIMITIVE_COLORS.resioBlue,
    },
    icon: {
      primary: '#333333',
      secondary: '#777777',
      muted: '#999999',
      inactive: '#BBBBBB',
      inverse: '#FFFFFF',
    },
    shadow: {
      card: 'rgba(0,0,0,0.10)',
      button: 'rgba(0,0,0,0.08)',
      dropdown: 'rgba(0,0,0,0.12)',
      fab: 'rgba(0,0,0,0.14)',
      modal: 'rgba(0,0,0,0.18)',
      focusRing: 'rgba(70,114,254,0.35)',
    },
  },

  components: {
    nav: {
      background: '#FFFFFF',
      activeBackground: '#F0F1F3',
      activeBorder: PRIMITIVE_COLORS.resioBlue,
      inactiveBorder: '#EEEEEE',
      headerBlue: PRIMITIVE_COLORS.resioBlue,
      headerPurple: PRIMITIVE_COLORS.resioPurple,
    },
    button: {
      primaryBg: PRIMITIVE_COLORS.resioBlue,
      primaryText: '#FFFFFF',
      secondaryBg: 'transparent',
      secondaryBorder: PRIMITIVE_COLORS.resioBlue,
      secondaryText: PRIMITIVE_COLORS.resioBlue,
      dangerBg: PRIMITIVE_COLORS.resioPink,
      dangerText: '#FFFFFF',
      outlineBg: 'transparent',
      outlineBorder: '#333333',
      outlineText: '#333333',
      disabledBg: '#E0E0E0',
      disabledText: '#999999',
    },
    card: {
      background: '#FFFFFF',
      border: '#E0E0E0',
      readBackground: 'transparent',
      readBorder: '#EEEEEE',
    },
    input: {
      background: '#FFFFFF',
      border: '#CCCCCC',
      focusBorder: PRIMITIVE_COLORS.resioBlue,
      placeholder: '#999999',
      text: '#1A1A1A',
    },
    toggle: {
      background: '#F0F1F3',
      activeBackground: '#E0E0E0',
      trackOff: '#D5D5D5',
      trackOn: PRIMITIVE_COLORS.resioBlue,
      thumb: '#FFFFFF',
    },
    avatar: {
      background: 'rgba(0,0,0,0.06)',
      text: '#333333',
      placeholder: '#F0F1F3',
    },
    chat: {
      sentBubble: PRIMITIVE_COLORS.resioBlue,
      receivedBubble: '#F0F1F3',
      inputBackground: 'rgba(0,0,0,0.04)',
      timestamp: 'rgba(0,0,0,0.45)',
    },
    notification: {
      unreadDot: PRIMITIVE_COLORS.warningOrange,
      unreadBackground: 'rgba(0,0,0,0.04)',
    },
    checkbox: {
      background: '#FFFFFF',
      border: '#CCCCCC',
      selected: PRIMITIVE_COLORS.resioBlue,
      checkmark: '#FFFFFF',
    },
    // The three values here have to agree with each other: `background` is the
    // pill the dots sit on, so its luminance decides which way the dots go.
    // It used to be a dark scrim copied from the dark theme while the dots
    // assumed a light one, and the two cancelled out — over a dark or mid photo
    // the ACTIVE dot measured 1.64–1.66:1 while the inactive dots sat at
    // 4.79–13.08:1, i.e. the affordance ran backwards. A light scrim is also what
    // this theme already uses for controls over imagery (`glass.buttonBgImage`).
    carousel: {
      dotActive: '#333333',
      // #CCCCCC was invisible on every backdrop this token actually meets:
      // 1.50:1 on the glass pill in RecentlyViewedSection, 1.61:1 on the scrim.
      // #777777 gives 4.18:1 and 2.44–4.48:1 respectively, while staying clearly
      // subordinate to dotActive (6.87:1 and up).
      dotInactive: '#777777',
      // 0.75 rather than the 0.65 of `glass.buttonBgImage`: it is what holds the
      // active dot above 6.8:1 even over a solid black photo.
      background: 'rgba(255,255,255,0.75)',
    },
    badge: {
      priceBg: 'rgba(0,0,0,0.55)',
      attendingBg: 'rgba(76,175,80,0.1)',
      attendingText: '#4CAF50',
      purpleBg: 'rgba(159,68,211,0.1)',
      purpleText: PRIMITIVE_COLORS.resioPurple,
    },
    gradient: {
      orangeadeStart: '#EF303D',
      orangeadeEnd: '#073EEC',
    },
    admin: adminLightComponents,
  },

  typography: TYPOGRAPHY,
  spacing: SPACING,
  radius: RADIUS,
  shadow: SHADOW_SHAPES,
  motion: MOTION,
};
